import CheckIcon from "@redotech/redo-icons/check-circle.svg";
import type { ProductResumeState } from "@redotech/redo-onboarding-api/schema/get-resume-state";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import type { ProductEntry } from "../products";
import * as styles from "./landing-product-pitch.module.css";
import {
  getLandingProductHighlights,
  getLandingProductSubtext,
} from "./landing-products";

interface LandingProductPitchProps {
  catalogProduct?: ProductEntry;
  isActive: boolean;
  product?: ProductEntry;
  progress?: Pick<ProductResumeState, "completedSteps" | "totalSteps"> | null;
}

export const LandingProductPitch = memo(function LandingProductPitch({
  catalogProduct,
  isActive,
  product,
  progress,
}: LandingProductPitchProps) {
  const highlights = useMemo(
    () =>
      product && catalogProduct
        ? getLandingProductHighlights(
            isActive ? catalogProduct : product,
            catalogProduct,
          )
        : [],
    [catalogProduct, isActive, product],
  );

  if (!product) {
    return (
      <Flex
        align="center"
        className={styles.panel}
        dir="column"
        flex={1}
        gap="md"
        justify="center"
      >
        <Text fontSize="sm" fontWeight="medium" textAlign="center">
          Select a product to begin
        </Text>
        <Text fontSize="sm" textAlign="center" textColor="secondary">
          You’ll see the setup path and what to expect before you continue.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      className={styles.panel}
      dir="column"
      flex={1}
      gap="5xl"
      overflowY="auto"
    >
      <Flex dir="column" gap="3xl">
        <Flex dir="column" gap="md">
          <Text
            as="h2"
            className={styles.heading}
            fontWeight="medium"
            textColor="primary"
          >
            {isActive ? product.label : `Set up ${product.label}`}
          </Text>
          <Text fontSize="md" textColor="secondary">
            {product.description}
          </Text>
        </Flex>
        {!isActive && (
          <Flex bgColor="secondary" borderRadius="lg" px="lg" py="md" w="full">
            <Text fontSize="sm" fontWeight="medium" textColor="primary">
              {getLandingProductSubtext(product, { isActive, progress })}
            </Text>
          </Flex>
        )}
      </Flex>

      <Flex dir="column" gap="lg">
        <Text fontSize="sm" fontWeight="medium" textColor="primary">
          Setup will cover
        </Text>
        <Flex dir="column" gap="lg">
          {highlights.map((highlight) => (
            <Flex align="flex-start" gap="md" key={highlight.label}>
              <RedoIcon
                className={styles.checkIcon}
                Icon={CheckIcon}
                size={18}
              />
              <Flex dir="column" gap="xxs" minW="none">
                <Text fontSize="sm" fontWeight="medium" textColor="primary">
                  {highlight.label}
                </Text>
                {highlight.description && (
                  <Text fontSize="xs" lineHeight="1.5" textColor="secondary">
                    {highlight.description}
                  </Text>
                )}
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
});
